import history from '@history';
import { PipelinesPaths } from 'app/configs/routesConfig/pipelines';
import { crmContactApi, loanPipelinesApi } from 'app/services/api';
import { loanPipelinesEventEmitter } from 'app/services/eventEmitter';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { LoanPipelinesEmitterEventType } from 'constants/loanPipelines';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

import { resetPipelineEntitiesAction } from './loanPipelineEntities.actions';

const pipelinesSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: 'id',
    processStrategy: pipeline => ({
      ...pipeline,
      countOfItems: _.get(pipeline, ['representationView', 'statistic', '0', 'countOfItems'], 0),
    }),
  },
);

export const LOAN_PIPELINES_ACTION_TYPE = createNamedActions('loanPipelines', {
  GET_LOAN_PIPELINES: 'GET_LOAN_PIPELINES',
  CREATE_LOAN_PIPELINE: 'CREATE_LOAN_PIPELINE',
  EDIT_LOAN_PIPELINE: 'EDIT_LOAN_PIPELINE',
  DELETE_LOAN_PIPELINE: 'DELETE_LOAN_PIPELINE',
  SET_ACTIVE_LOAN_PIPELINE_PARAMS: 'SET_ACTIVE_LOAN_PIPELINE_PARAMS',
  FETCH_KANBAN_VIEW_DATA: 'FETCH_KANBAN_VIEW_DATA',
  FETCH_KANBAN_VIEW_DATA_SUCCESS: 'FETCH_KANBAN_VIEW_DATA_SUCCESS',
  FETCH_KANBAN_VIEW_DATA_FAILURE: 'FETCH_KANBAN_VIEW_DATA_FAILURE',
  MOVE_KANBAN_CARD: 'MOVE_KANBAN_CARD',
});

export const getLoanPipelinesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await loanPipelinesApi.getLoanPipelines(params);

  return dispatch({
    type: LOAN_PIPELINES_ACTION_TYPE.GET_LOAN_PIPELINES,
    payload: normalize(data, [pipelinesSchema]).entities.data,
  });
});

export const createLoanPipelineAction = data => ({
  type: LOAN_PIPELINES_ACTION_TYPE.CREATE_LOAN_PIPELINE,
  payload: { countOfItems: 0, ...data },
});

export const editLoanPipelineAction = data => ({
  type: LOAN_PIPELINES_ACTION_TYPE.EDIT_LOAN_PIPELINE,
  payload: data,
});

export const deleteLoanPipelineAction = id => dispatch => {
  const { params } = PipelinesPaths.loanPipelinesById.matchPath(history.location.pathname) || {};

  const isActiveLoanPipeline = params?.id === id;

  if (isActiveLoanPipeline) {
    dispatch(resetPipelineEntitiesAction());
  }

  return dispatch({
    type: LOAN_PIPELINES_ACTION_TYPE.DELETE_LOAN_PIPELINE,
    meta: { id },
  });
};

export const fetchKanbanViewDataAction = catchAsyncActionError(
  ({ pipelineId, searchInput, columnFilters } = {}) =>
    async dispatch => {
      dispatch({
        type: LOAN_PIPELINES_ACTION_TYPE.FETCH_KANBAN_VIEW_DATA,
        payload: { pipelineId },
      });

      try {
        const { data } = await loanPipelinesApi.getKanbanViewData({
          loanPipelineId: pipelineId,
          searchInput,
          columnFilters,
        });

        return dispatch({
          type: LOAN_PIPELINES_ACTION_TYPE.FETCH_KANBAN_VIEW_DATA_SUCCESS,
          payload: { pipelineId, data },
        });
      } catch (error) {
        dispatch({
          type: LOAN_PIPELINES_ACTION_TYPE.FETCH_KANBAN_VIEW_DATA_FAILURE,
          payload: { pipelineId },
        });

        throw error;
      }
    },
);

const moveKanbanCardAction = ({ pipelineId, entryId, fromColumnId, toColumnId }) => ({
  type: LOAN_PIPELINES_ACTION_TYPE.MOVE_KANBAN_CARD,
  payload: { pipelineId, entryId, fromColumnId, toColumnId },
});

export const moveContactLifecycleAction =
  ({ pipelineId, entryId, fromColumnId, toColumnId }) =>
  async dispatch => {
    if (fromColumnId === toColumnId) {
      return;
    }

    dispatch(moveKanbanCardAction({ pipelineId, entryId, fromColumnId, toColumnId }));

    try {
      await crmContactApi.updateContactLifecycle({
        contactId: entryId,
        newStage: toColumnId,
      });
    } catch {
      dispatch(
        moveKanbanCardAction({
          pipelineId,
          entryId,
          fromColumnId: toColumnId,
          toColumnId: fromColumnId,
        }),
      );

      dispatch(
        showMessageAction({ message: 'Failed to update contact lifecycle', variant: 'error' }),
      );
    }
  };

export const claimLeadAction = entryId => async () => {
  try {
    await crmContactApi.claimContact(entryId);

    return { success: true, status: HTTP_RESPONSE_STATUS_CODES.SUCCESS };
  } catch (err) {
    const status = err?.response?.status;
    const serverMessage = err?.response?.data?.message;

    return { success: false, status, serverMessage };
  }
};

export const rebuiltLoanPipelineAction = data => dispatch => {
  const { params } = PipelinesPaths.loanPipelinesById.matchPath(history.location.pathname) || {};

  const isActiveLoanPipeline = params?.id === data.id;

  dispatch(editLoanPipelineAction(data));

  if (isActiveLoanPipeline) {
    dispatch(resetPipelineEntitiesAction());
    loanPipelinesEventEmitter.emit(LoanPipelinesEmitterEventType.RE_FETCH_ENTITIES, {
      isFirstFetch: true,
    });
  }
};
